// AnniversaryDiaryList — reverse-lookup of diaries from this anniversary
// (W3.1 §5.2 + §6.1). Calls /api/v1/anniversaries/{id}/diaries.

import { Link, useNavigate } from "react-router-dom";
import { BookOpen, Plus } from "lucide-react";

import { useAnniversaryDiaries } from "@/lib/hooks/anniversary";
import { previewFromHtml } from "@/lib/utils/text";
import { LoadingState } from "@/components/primitives/LoadingState";
import { EmptyState } from "@/components/primitives/EmptyState";
import { Card } from "@/components/ui/card";

export interface AnniversaryDiaryListProps {
  anniversaryId: string;
}

export function AnniversaryDiaryList({ anniversaryId }: AnniversaryDiaryListProps) {
  const nav = useNavigate();
  const query = useAnniversaryDiaries(anniversaryId);
  const items = query.data?.items ?? [];

  return (
    <section
      data-component="AnniversaryDiaryList"
      className="flex flex-col gap-3"
      data-testid="anniversary-diary-list"
    >
      <h3 className="font-display text-title-3 text-ink-1">相關日記</h3>
      {query.isLoading ? (
        <LoadingState count={2} />
      ) : items.length === 0 ? (
        <EmptyState
          icon={<BookOpen size={22} aria-hidden />}
          halo="partner"
          title="還沒有從這天衍生的日記"
          description="把今天的回憶寫下來吧"
          actionLabel="為這個紀念日寫日記"
          onAction={() =>
            nav(`/diaries/new#source=anniversary&anniversary_id=${anniversaryId}`)
          }
        />
      ) : (
        <ul className="flex flex-col gap-2">
          {items.map((d) => (
            <li key={d.id}>
              {/*
                W53.2 D-1/D-2 — the card is a `<Card>` with the `<Link>` **inside**
                it, not the other way round, and that order is the fix rather
                than a style choice.

                The old string was `block rounded-xl surface-plain p-3 border
                border-hairline hover:bg-black/5 press` — so the hover was
                **dead**. `.surface-plain` is declared as an unlayered rule and
                Tailwind utilities live in `@layer utilities`, so on the same
                element `bg-black/5` loses and the row never darkened. That is
                the same specificity trap `card.tsx`'s `bare` docblock and
                `BARE_CARD` both record, on a row nobody had looked at.

                Splitting the two elements fixes it without changing the paint:
                the parent paints `surface-plain`, the child (the link) carries
                the hover. Two elements, two jobs, both effective.
              */}
              <Card className="overflow-hidden">
                <Link
                  to={`/diaries/${d.id}`}
                  className="block p-3 hover:bg-black/5 press"
                  data-testid={`anniversary-diary-${d.id}`}
                >
                  <p className="text-meta text-ink-3 tabular-nums">{d.date}</p>
                  <p className="text-body-sm text-ink-1 font-semibold truncate">
                    {d.title || "（無標題）"}
                  </p>
                  {d.excerpt ? (
                    <p className="text-meta text-ink-2 mt-1 line-clamp-2">
                      {previewFromHtml(d.excerpt, 80)}
                    </p>
                  ) : null}
                </Link>
              </Card>
            </li>
          ))}
        </ul>
      )}
      <span className="hidden" aria-hidden>
        <Plus size={14} />
      </span>
    </section>
  );
}